import type { FunctionComponent } from 'react';
import { Alert, Button, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { useTranslation } from 'i18n';
import { breakpoints } from 'styles/theme.variables';
import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import { WarningIconSm } from '@chownow/pantry/icons';
import { hasDrawerIntegration } from './marketplaceConfig';
import type { CropState } from './EditPhotoContent';
import { CroppedImagePreview } from './CroppedImagePreview';
import { MarketplacePreviewCards } from './MarketplacePreviewCards';

const NS = 'menuPhotosPhotoDrawer';

type PreviewPhotoContentProps = {
  imageUrl: string;
  cropState: CropState;
  isSaving: boolean;
  saveError: string | null;
  activeIntegrationNames?: Set<string>;
  onSave: () => void;
  onBack: () => void;
};

export const PreviewPhotoContent: FunctionComponent<PreviewPhotoContentProps> = ({
  imageUrl,
  cropState,
  isSaving,
  saveError,
  activeIntegrationNames,
  onSave,
  onBack,
}) => {
  const isThirdParty = hasDrawerIntegration(activeIntegrationNames);
  const { t } = useTranslation(NS);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  const dockButtons = (
    <>
      <Button
        radius="xl"
        tt="uppercase"
        variant="outline"
        style={{ letterSpacing: 'var(--letterspacing-wide)' }}
        disabled={isSaving}
        onClick={onBack}
      >
        {t('PreviewPhotoContent.back')}
      </Button>
      <Button
        radius="xl"
        tt="uppercase"
        bg="var(--surface-inverse-primary)"
        c="var(--content-inverse-primary)"
        style={{ letterSpacing: 'var(--letterspacing-wide)' }}
        disabled={isSaving}
        onClick={onSave}
      >
        {t('PreviewPhotoContent.save')}
      </Button>
    </>
  );

  return (
    // Type DockedDrawerFormContent (.jsx) to remove this suppression.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <DockedDrawerFormContent stacked={isMobile} dockButtons={dockButtons}>
      <Stack gap="md">
        <Text>{t('PreviewPhotoContent.label')}</Text>
        {saveError && (
          <Alert variant="caution" icon={<WarningIconSm />}>
            {saveError}
          </Alert>
        )}
        {isThirdParty ? (
          <MarketplacePreviewCards
            imageUrl={imageUrl}
            cropState={cropState}
            activeIntegrationNames={activeIntegrationNames ?? new Set()}
          />
        ) : (
          <CroppedImagePreview imageUrl={imageUrl} cropState={cropState} />
        )}
      </Stack>
    </DockedDrawerFormContent>
  );
};
